import isArray from "lodash/isArray";
import find from "lodash/find";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import toNumber from "lodash/toNumber";

// Get flat Array of answers from all questions answers
// This handles multichoice answers
const getAllAnswers = (state) => {
  return state.answers.reduce((acc, curr) => {
    const { answer, ...other } = curr;
    const answers = answer.map((a) => ({ ...a, ...other }));
    return [...acc, ...answers];
  }, []);
};

// Gets an array of all specifications related to an answer
// Filters out slider answer specifications based upon range
const getAnswerData = (quizzes, answer) => {
  if (answer.id === "other") return [[], []];

  const quiz = find(quizzes, { id: answer.quiz });

  const { specifications, relevance } = find(quiz.answers, { id: answer.id });

  // Filter out specifications/relevance that aren't in range
  if (answer.type === "slider") {
    const filterByRange = (spec) => {
      const min = toNumber(spec.min);
      const max = toNumber(spec.max);

      if (!min && !max) return true;
      if (!min && max) return answer.value <= max;
      if (min && !max) return answer.value >= min;

      return answer.value >= min && answer.value <= max;
    };

    return [
      specifications.filter(filterByRange),
      relevance.filter(filterByRange),
    ];
  }

  return [specifications, relevance];
};

// Checks all the specifications that match, and returns the highest one to be applied
const getHighestSpecValue = (product, answer) => {
  const highestSpecValue = answer.specifications.reduce((acc, spec) => {
    let value = get(product.specs, spec.title, null);

    if (!value) return acc;

    // Convert value to array, to handle multi specs
    value = isArray(value) ? value : [value];

    if (!value.includes(spec.specification)) return acc;

    const multiplier = toNumber(spec.multiplier);

    return !acc || multiplier > acc ? multiplier : acc;
  }, null);

  return highestSpecValue || 0;
};

// Check all result relevences for an answer, make sure product contains spec, then apply weighting
const getResultRelevances = (product, answer, weighting) => {
  return answer.relevance.reduce((acc, rel) => {
    let value = get(product.specs, rel.title, null);

    if (!value) return acc;

    // Convert value to array, to handle multi specs
    value = isArray(value) ? value : [value];

    if (!value.includes(rel.specification)) return acc;

    return [...acc, { ...rel, weighting }];
  }, []);
};

/**
 * Adds an "allSpecValue" to all products based upon answers from Quiz
 * Also orders the array based upon "allSpecValue"
 */
const getAllSpecValues = (products, state) => {
  let answers = getAllAnswers(state);
  answers = answers.filter((a) => a.type !== "budget");

  // Get Array of all answers, and their specifications
  answers = answers.map((answer) => {
    const [specifications, relevance] = getAnswerData(state.quizzes, answer);
    return { ...answer, specifications, relevance };
  });

  // Loop products
  products = products.map((product) => {
    let { allSpecValue, relevance } = answers.reduce(
      (acc, answer) => {
        const specValue = getHighestSpecValue(product, answer);
        const relevance = getResultRelevances(product, answer, specValue);

        return {
          allSpecValue: acc.allSpecValue + specValue,
          relevance: [...acc.relevance, ...relevance],
        };
      },
      { allSpecValue: 1, relevance: [] }
    );

    relevance = orderBy(relevance, "weighting", "desc");

    // Apply allSpecValue (int)
    return { ...product, allSpecValue, relevance };
  });

  return orderBy(products, "allSpecValue", "desc");
};

export default getAllSpecValues;
